/**
 * Pure mapping between the manager (shell) URL space and an embedded app's own
 * URL space. Contract: specs/016-global-nav-venue-sync/contracts/manager-url-scheme.md
 *
 * Shell:  /{appId}/{venueSlug}[/{childPath...}][?query]
 * Child:  /{venueSlug}[/{childPath...}][?query]
 */

export function parentToChildPath(appId: string, pathname: string, search = ''): string {
  const prefix = `/${appId}`;

  if (pathname === prefix) {
    return `/${search}`;
  }

  if (pathname.startsWith(`${prefix}/`)) {
    return pathname.slice(prefix.length) + search;
  }

  // Mounted on a non-matching route; fall back to the child root.
  return `/${search}`;
}

export function childToParentPath(appId: string, childPath: string): string {
  const normalized = childPath.startsWith('/') ? childPath : `/${childPath}`;
  return `/${appId}${normalized === '/' ? '' : normalized}`;
}

/**
 * Target child path for a same-application venue switch (FR-006a).
 *
 * - Same role at both venues: keep the first path segment after the venue slug
 *   (the "section", e.g. /venue-a/incidents/123/view → /venue-b/incidents).
 * - Different role, or no section: the new venue's home.
 *
 * Deeper record-specific segments and the query string are always dropped —
 * they belong to the previous venue's data.
 */
export function computeSwitchTarget(
  currentChildPath: string,
  newVenueSlug: string,
  sameRole: boolean,
): string {
  if (!sameRole) {
    return `/${newVenueSlug}`;
  }

  const pathOnly = currentChildPath.split('?')[0] ?? '';
  const segments = pathOnly.split('/').filter(Boolean);
  const section = segments[1];

  return section ? `/${newVenueSlug}/${section}` : `/${newVenueSlug}`;
}
